import React, { useCallback } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { useTellYourStoryPrompt } from '@/organisms/TellYourStoryPrompt/TellYourStoryPrompt'
import { Offer } from '@/services/PifService'
import { getStripe } from '@/services/StripeService/StripePaymentService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { PayItForwardStripeForm } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm'
import { useClientSecret } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useClientSecret'
import { PayItForwardFormProvider } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm/PayItForwardFormContext'
import { usePayItForwardState } from './usePayItForwardState'

export interface PaymentMethodExperimentProps {
  projectSlug: string
  offer: Offer
  isShowThanksForSharing: boolean
  nameInCreditsRequested?: string
}

export const PaymentMethods: React.FC<PaymentMethodExperimentProps> = ({
  projectSlug,
  offer,
  isShowThanksForSharing,
  nameInCreditsRequested,
}) => {
  const { locale } = useLocale()
  const { onComplete: onCompletePifState } = usePayItForwardState()
  const { openModal: openTellYourStoryModal, setRelatedOrderId } = useTellYourStoryPrompt()
  const { user } = useUser()
  const { clientSecret } = useClientSecret({ offer })

  const openTellYourStoryModalWhenApplicable = useCallback(() => {
    if (isShowThanksForSharing && Boolean(user)) {
      openTellYourStoryModal()
    }
  }, [isShowThanksForSharing, openTellYourStoryModal, user])

  const onComplete = useCallback(
    ({ orderId }: { orderId: string }) => {
      onCompletePifState()
      setRelatedOrderId(orderId)
      openTellYourStoryModalWhenApplicable()
    },
    [onCompletePifState, openTellYourStoryModalWhenApplicable, setRelatedOrderId],
  )

  if (clientSecret) {
    return (
      <Elements stripe={getStripe(locale)} options={{ loader: 'auto', clientSecret }}>
        <PayItForwardFormProvider
          onComplete={onComplete}
          offer={offer}
          projectSlug={projectSlug}
          user={user}
          nameInCreditsRequested={nameInCreditsRequested}
        >
          <PayItForwardStripeForm clientSecret={clientSecret} offer={offer} projectSlug={projectSlug} />
        </PayItForwardFormProvider>
      </Elements>
    )
  }
  return null
}
